import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SignaturePad } from "@/components/ui/SignaturePad";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Check,
  X,
  Building2,
  Calendar,
  FileText,
  CheckCircle2,
  XCircle,
  Download,
} from "lucide-react";

export const Route = createFileRoute("/invoices/approve/$token")({
  component: PublicInvoiceApprovalPage,
});

type Invoice = {
  id: string;
  number: string;
  title: string;
  description: string | null;
  issue_date: string;
  due_date: string | null;
  currency: string;
  tax_percent: number;
  subtotal: number;
  tax_amount: number;
  total: number;
  notes: string | null;
  clients: { name: string; email: string | null; company_name: string | null } | null;
  companies: { name: string; logo_url: string | null; email: string | null } | null;
};

type Item = {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
};

type TokenRow = {
  id: string;
  invoice_id: string;
  status: "sent" | "approved" | "rejected" | "revoked";
  responded_at: string | null;
  response_notes: string | null;
  pdf_url: string | null;
};

function PublicInvoiceApprovalPage() {
  const { token } = Route.useParams();
  const { t } = useTranslation();
  const [tokenRow, setTokenRow] = useState<TokenRow | null>(null);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState("");
  const [signName, setSignName] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: payload, error: rpcErr } = await supabase.rpc("get_invoice_for_approval", {
        p_token: token,
      });
      if (cancelled) return;
      if (rpcErr || !payload) {
        setError(
          t("pages.invoiceApproval.invalidLink", {
            defaultValue: "This link is invalid or no longer available.",
          }),
        );
        setLoading(false);
        return;
      }
      const tk = (payload as { token: TokenRow }).token;
      const inv = (payload as { invoice: Invoice }).invoice;
      const its = (payload as { items: Item[] }).items;
      setTokenRow(tk);
      setInvoice(inv ?? null);
      setItems(its ?? []);
      if (inv?.clients?.name && !signName) setSignName(inv.clients.name);
      setLoading(false);
      // Record that the client opened the link (fire-and-forget)
      void supabase.rpc("mark_invoice_token_viewed", { p_token: token });
    })();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const respond = async (decision: "approved" | "rejected") => {
    if (!tokenRow) return;
    if (decision === "approved") {
      if (!signName.trim()) {
        toast.error(t("pages.signature.signerNameRequired"));
        return;
      }
      if (!signature) {
        toast.error(t("pages.signature.signatureRequired"));
        return;
      }
    }
    setSubmitting(true);
    const respondedAt = new Date().toISOString();
    const trimmedNotes = notes.trim() || null;
    const { error } = await supabase.rpc("respond_to_invoice_token", {
      _token: token,
      _decision: decision,
      _response_notes: trimmedNotes ?? "",
      _client_signature_name: decision === "approved" ? signName.trim() : "",
      _client_signature_data: decision === "approved" ? (signature ?? "") : "",
    });
    setSubmitting(false);
    if (error) {
      toast.error(
        t("pages.invoiceApproval.submitFailed", {
          defaultValue: "Could not submit your response",
        }),
        {
          description:
            error.message ??
            t("pages.invoiceApproval.submitFailedDefault", {
              defaultValue: "Please try again later.",
            }),
        },
      );
      return;
    }
    setTokenRow({
      ...tokenRow,
      status: decision,
      response_notes: trimmedNotes,
      responded_at: respondedAt,
    } as TokenRow);
    toast.success(
      decision === "approved"
        ? t("pages.invoiceApproval.approvedToast", {
            defaultValue: "Invoice confirmed. Thank you!",
          })
        : t("pages.invoiceApproval.rejectedToast", {
            defaultValue: "Response recorded.",
          }),
    );
  };

  const formatMoney = (v: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: invoice?.currency || "USD",
    }).format(Number(v) || 0);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !tokenRow || !invoice) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="max-w-md rounded-lg border border-border bg-card p-8 text-center">
          <XCircle className="mx-auto h-10 w-10 text-destructive" />
          <h1 className="mt-3 text-xl font-semibold">
            {t("pages.invoiceApproval.linkUnavailable", {
              defaultValue: "Link unavailable",
            })}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {error ??
              t("pages.invoiceApproval.loadFailed", {
                defaultValue: "We couldn't load this invoice.",
              })}
          </p>
        </div>
      </div>
    );
  }

  const alreadyResponded = tokenRow.status !== "sent";
  const wasApproved = tokenRow.status === "approved";
  const wasRejected = tokenRow.status === "rejected";
  const wasRevoked = tokenRow.status === "revoked";

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <div className="mx-auto max-w-3xl space-y-6">
        {/* Company header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {invoice.companies?.logo_url ? (
              <img
                src={invoice.companies.logo_url}
                alt={invoice.companies.name}
                className="h-10 w-10 rounded object-cover"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded bg-primary/10">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
            )}
            <div>
              <p className="text-sm font-semibold">
                {invoice.companies?.name ??
                  t("pages.invoiceApproval.invoiceFallback", {
                    defaultValue: "Invoice",
                  })}
              </p>
              {invoice.companies?.email && (
                <p className="text-xs text-muted-foreground">{invoice.companies.email}</p>
              )}
            </div>
          </div>
          <Badge variant="outline" className="font-mono text-xs">
            {invoice.number}
          </Badge>
        </div>

        {/* Status banner */}
        {alreadyResponded && (
          <div
            className={`flex items-start gap-3 rounded-lg border p-4 ${
              wasApproved
                ? "border-primary/30 bg-primary/5"
                : wasRejected
                  ? "border-destructive/30 bg-destructive/5"
                  : "border-border bg-muted"
            }`}
          >
            {wasApproved ? (
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary" />
            ) : (
              <XCircle className="mt-0.5 h-5 w-5 text-destructive" />
            )}
            <div className="flex-1">
              <p className="font-medium">
                {wasApproved &&
                  t("pages.invoiceApproval.youApproved", {
                    defaultValue: "You confirmed this invoice",
                  })}
                {wasRejected &&
                  t("pages.invoiceApproval.youDeclined", {
                    defaultValue: "You disputed this invoice",
                  })}
                {wasRevoked &&
                  t("pages.invoiceApproval.linkRevoked", {
                    defaultValue: "This link was revoked",
                  })}
              </p>
              {tokenRow.responded_at && (
                <p className="text-xs text-muted-foreground">
                  {new Date(tokenRow.responded_at).toLocaleString()}
                </p>
              )}
              {tokenRow.response_notes && (
                <p className="mt-2 text-sm whitespace-pre-wrap">{tokenRow.response_notes}</p>
              )}
            </div>
          </div>
        )}

        {/* Invoice card */}
        <div className="rounded-lg border border-border bg-card p-6">
          <h1 className="text-2xl font-semibold tracking-tight">{invoice.title}</h1>
          {invoice.clients?.name && (
            <p className="mt-1 text-sm text-muted-foreground">
              {t("pages.invoiceApproval.preparedFor", {
                name: invoice.clients.name,
                defaultValue: `Prepared for ${invoice.clients.name}`,
              })}
              {invoice.clients.company_name && ` — ${invoice.clients.company_name}`}
            </p>
          )}

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="flex items-start gap-2">
              <Calendar className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {t("pages.invoiceApproval.issued", { defaultValue: "Issued" })}
                </p>
                <p className="text-sm font-medium">
                  {new Date(invoice.issue_date).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Calendar className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {t("pages.invoiceApproval.dueDate", { defaultValue: "Due date" })}
                </p>
                <p className="text-sm font-medium">
                  {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : "—"}
                </p>
              </div>
            </div>
          </div>

          {invoice.description && (
            <div className="mt-5 border-t border-border pt-4">
              <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                {t("pages.invoiceApproval.description", {
                  defaultValue: "Description",
                })}
              </p>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{invoice.description}</p>
            </div>
          )}
        </div>

        {/* Items */}
        <div className="rounded-lg border border-border bg-card">
          <div className="border-b border-border p-4">
            <p className="flex items-center gap-2 text-sm font-semibold">
              <FileText className="h-4 w-4" />{" "}
              {t("pages.invoiceApproval.lineItems", {
                defaultValue: "Line items",
              })}
            </p>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t("pages.invoiceApproval.descCol", {
                      defaultValue: "Description",
                    })}
                  </TableHead>
                  <TableHead className="w-20 text-right">
                    {t("pages.invoiceApproval.qty", { defaultValue: "Qty" })}
                  </TableHead>
                  <TableHead className="w-32 text-right">
                    {t("pages.invoiceApproval.unitPrice", {
                      defaultValue: "Unit price",
                    })}
                  </TableHead>
                  <TableHead className="w-32 text-right">
                    {t("pages.invoiceApproval.total", { defaultValue: "Total" })}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.id}>
                    <TableCell>{it.description}</TableCell>
                    <TableCell className="text-right">{it.quantity}</TableCell>
                    <TableCell className="text-right">{formatMoney(it.unit_price)}</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatMoney(it.total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-col items-end gap-1 border-t border-border p-4 text-sm">
            <div className="flex w-56 justify-between">
              <span className="text-muted-foreground">
                {t("pages.invoiceApproval.subtotal", {
                  defaultValue: "Subtotal",
                })}
              </span>
              <span>{formatMoney(invoice.subtotal)}</span>
            </div>
            <div className="flex w-56 justify-between">
              <span className="text-muted-foreground">
                {t("pages.invoiceApproval.taxLabel", {
                  percent: invoice.tax_percent,
                  defaultValue: `Tax (${invoice.tax_percent}%)`,
                })}
              </span>
              <span>{formatMoney(invoice.tax_amount)}</span>
            </div>
            <div className="mt-1 flex w-56 justify-between border-t border-border pt-1 text-base font-semibold">
              <span>{t("pages.invoiceApproval.totalLabel", { defaultValue: "Total" })}</span>
              <span>{formatMoney(invoice.total)}</span>
            </div>
          </div>
        </div>

        {invoice.notes && (
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
              {t("pages.invoiceApproval.notes", { defaultValue: "Notes" })}
            </p>
            <p className="text-sm whitespace-pre-wrap">{invoice.notes}</p>
          </div>
        )}

        {/* Signed PDF download (generated with the client's signature once approved) */}
        {wasApproved && (
          <div className="flex flex-col items-stretch gap-2 rounded-lg border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded bg-primary/10">
                <FileText className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium">
                  {t("pages.invoiceApproval.signedPdfTitle", {
                    defaultValue: "Signed invoice PDF",
                  })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("pages.invoiceApproval.signedPdfHint", {
                    defaultValue: "Download a copy with your signature for your records",
                  })}
                </p>
              </div>
            </div>
            <Button asChild className="gap-2">
              <a
                href={`/api/public/invoice-pdf/${token}?download=1`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Download className="h-4 w-4" />
                {t("pages.invoiceApproval.downloadSignedPdf", {
                  defaultValue: "Download signed PDF",
                })}
              </a>
            </Button>
          </div>
        )}

        {/* Unsigned PDF copy (for review before signing) */}
        {!wasApproved && tokenRow.pdf_url && (
          <div className="flex flex-col items-stretch gap-2 rounded-lg border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded bg-primary/10">
                <FileText className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium">
                  {t("pages.invoiceApproval.pdfTitle", {
                    defaultValue: "Invoice PDF",
                  })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("pages.invoiceApproval.pdfHint", {
                    defaultValue: "Download a copy for your records",
                  })}
                </p>
              </div>
            </div>
            <Button asChild variant="outline" className="gap-2">
              <a href={tokenRow.pdf_url} target="_blank" rel="noopener noreferrer" download>
                <Download className="h-4 w-4" />
                {t("pages.invoiceApproval.downloadPdf", {
                  defaultValue: "Download PDF",
                })}
              </a>
            </Button>
          </div>
        )}

        {/* Action panel */}
        {!alreadyResponded && (
          <div className="rounded-lg border border-border bg-card p-6">
            <p className="text-sm font-semibold">
              {t("pages.invoiceApproval.yourResponse", {
                defaultValue: "Your response",
              })}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("pages.invoiceApproval.responseHint", {
                defaultValue:
                  "Confirm receipt of this invoice or let us know if there is an issue.",
              })}
            </p>
            <div className="mt-4 space-y-1.5">
              <Label htmlFor="signer-name">{t("pages.signature.signerNameLabel")}</Label>
              <Input
                id="signer-name"
                value={signName}
                onChange={(e) => setSignName(e.target.value)}
                placeholder={t("pages.signature.signerNamePlaceholder")}
              />
            </div>
            <div className="mt-4">
              <SignaturePad
                value={signature}
                onChange={setSignature}
                label={t("pages.signature.signatureLabel")}
                hint={t("pages.signature.signatureHint")}
                clearLabel={t("pages.signature.signatureClear")}
              />
            </div>
            <Textarea
              placeholder={t("pages.invoiceApproval.messagePlaceholder", {
                defaultValue: "Optional message",
              })}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-4"
              rows={3}
            />
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <Button
                onClick={() => respond("approved")}
                disabled={submitting}
                className="flex-1 gap-2"
              >
                <Check className="h-4 w-4" />
                {t("pages.invoiceApproval.approveBtn", {
                  defaultValue: "Confirm invoice",
                })}
              </Button>
              <Button
                onClick={() => respond("rejected")}
                disabled={submitting}
                variant="outline"
                className="flex-1 gap-2"
              >
                <X className="h-4 w-4" />
                {t("pages.invoiceApproval.declineBtn", {
                  defaultValue: "Dispute invoice",
                })}
              </Button>
            </div>
          </div>
        )}

        <p className="pt-2 text-center text-xs text-muted-foreground">
          {t("pages.invoiceApproval.poweredBy", {
            name: invoice.companies?.name ?? "Business Dite Ads",
            defaultValue: `Sent securely by ${invoice.companies?.name ?? "Business Dite Ads"}`,
          })}
        </p>
      </div>
    </div>
  );
}
